import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const ProfitAndLoss = () => {
  // State variables for Profit and Loss calculation
  const [revenue, setRevenue] = useState(0); // Total revenue
  const [expenses, setExpenses] = useState(0); // Total expenses
  const [profitLoss, setProfitLoss] = useState(0); // Net profit or loss result

  // Event handlers to update state values
  const handleRevenueChange = (e) =>
    setRevenue(parseFloat(e.target.value) || 0);
  const handleExpensesChange = (e) =>
    setExpenses(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate profit or loss when any input changes
  useEffect(() => {
    // Calculate profit or loss
    const calculatedProfitLoss = revenue - expenses;
    setProfitLoss(calculatedProfitLoss);

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [revenue, expenses]);

  return (
    <Layout title="Profit and Loss (P&L) Calculator" path="profit-loss">
      <section className="section-padding" id="pl-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Profit and Loss Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Profit and Loss (P&L) Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Revenue Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="revenue" className="form-label">
                        Revenue
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Revenue:</strong> Enter the total income generated from the sale of goods or services."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="revenue"
                        placeholder="Enter total revenue"
                        value={revenue || ""}
                        onChange={handleRevenueChange}
                      />
                    </div>

                    {/* Expenses Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="expenses" className="form-label">
                        Expenses
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Expenses:</strong> Enter the total costs incurred during business operations, including salaries, rent, etc."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="expenses"
                        placeholder="Enter total expenses"
                        value={expenses || ""}
                        onChange={handleExpensesChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Net Profit/Loss:{" "}
                        <span className="fw-bold ms-3">
                          {profitLoss === 0
                            ? "0.00"
                            : profitLoss.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Profit and Loss (P&L) Calculator
                </h3>

                {/* What is a Profit and Loss (P&L) Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Profit and Loss (P&L) Calculator?
                </h4>
                <p>
                  The **Profit and Loss (P&L) Calculator** is a tool that helps
                  businesses calculate their net profit or loss by subtracting
                  total expenses from total revenue. It provides a snapshot of a
                  company's financial health over a specified period.
                </p>

                {/* Why Use a Profit and Loss (P&L) Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Profit and Loss (P&L) Calculator?
                </h4>
                <p>
                  Using the P&L Calculator allows businesses to assess their
                  profitability, understand cost structures, and make informed
                  decisions regarding future investments and business
                  strategies.
                </p>

                {/* Benefits of Using a Profit and Loss (P&L) Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Profit and Loss (P&L) Calculator
                </h4>
                <ul>
                  <li>
                    <strong>
                      Essential for tracking business financial health:
                    </strong>{" "}
                    It helps businesses keep track of their profits and losses
                    regularly.
                  </li>
                  <li>
                    <strong>Aids in budgeting and financial planning:</strong>{" "}
                    Knowing profit or loss helps businesses create accurate
                    budgets and forecasts.
                  </li>
                  <li>
                    <strong>
                      Useful for financial performance assessment:
                    </strong>{" "}
                    Helps track performance over time and identify areas of
                    improvement.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Profit and Loss (P&L) Calculator?
                </h4>
                <p>The P&L Calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>All businesses:</strong> To regularly assess
                    financial health.
                  </li>
                  <li>
                    <strong>Small businesses:</strong> Especially useful for
                    startups and small businesses who need to track their
                    financial performance.
                  </li>
                  <li>
                    <strong>Startups:</strong> To monitor cash flow and ensure
                    profitability as they grow.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  Why is Profit and Loss Important for Businesses?
                </h4>
                <p>
                  Profit and loss statements are a crucial part of business
                  financial management. They provide insight into the overall
                  financial health, allowing business owners to make informed
                  decisions on cost reduction, pricing strategies, and business
                  operations.
                </p>

                <h4 className="mt-4 mb-3">
                  How Often Should a Business Use a Profit and Loss Calculator?
                </h4>
                <p>
                  Businesses should use the P&L Calculator regularly—monthly,
                  quarterly, or annually—depending on their size and needs.
                  Regularly reviewing profit and loss helps businesses stay on
                  top of their financial performance and quickly address any
                  concerns.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ProfitAndLoss;
